// eslint-disable-next-line import/named
import { constructAsync } from '../../../webmodule-common/other/async-constructor';
import { customElement, state } from 'lit/decorators.js';
import { EventGetReference, EventSnippet, EventTemplate } from '../../../webmodule-common/other/ui/events';
import { getApiFactory } from '../../api/api-injector';
import { html } from 'lit';
import { IconRefresh } from '../../../webmodule-common/other/ui/icons/icon-refresh';
import { moneyToTemplateResult } from '../../../webmodule-common/other/currency-formatter';
import { newGuid } from '../../../webmodule-common/other/api/guid';
import { TabManager } from '../../../webmodule-common/other/ui/tab-manager/tab-manager';
import type { TabPage } from '../../../webmodule-common/other/ui/tab-manager/tab-manager-types';
import { PurchaseOrderApi } from '../../api/purchase-order-api';
import { PurchaseOrderContainer } from '../data/purchase-order-container';
import { PurchaseOrderState, ViewPurchaseOrderTotalByState } from '../../api/dealer-api-interface-franchisee';
import { PurchaseOrderSummaryTable, PurchaseOrderSummaryTableOptions } from './purchase-order-summary-table';
import { PurchaseOrderView, PurchaseOrderViewOptions } from './purchase-order-view';
import { LangPurchaseOrderListView } from '../../../localization/scripts/purchase-orders/purchase-order-list-view-lang';
import { ViewBase } from '../../../webmodule-common/other/ui/view-base';

const numberOfDaysHistory = 30;

export interface PurchaseOrderListViewOptions {
  purchaseOrderOwnerId: EventGetReference;
}

@customElement('wm-purchaseorderlistview')
export class PurchaseOrderListView extends ViewBase {
  protected readonly purchaseOrderOwnerId: EventGetReference;
  protected tabManager: TabManager;
  private readonly elementId: string;
  protected tables: PurchaseOrderSummaryTable[];
  @state()
  protected purchaseOrderTotals: ViewPurchaseOrderTotalByState[] | null = null;
  private purchaseOrderApi: PurchaseOrderApi = getApiFactory().purchaseOrder();
  protected staleOrders: string[] = [];

  constructor(options: PurchaseOrderListViewOptions) {
    super();
    this.elementId = `purchase-order-list-view-${newGuid()}`;

    this.purchaseOrderOwnerId = options.purchaseOrderOwnerId;

    this.tables = this.initializeTables();
    this.tabManager = this.createTabManager();
  }

  public async setActiveTabByHash() {
    await this.tabManager?.applyWindowHash();
  }

  public async afterConstruction(): Promise<void> {
    await this.updatePageTotals();

    const table = this.tabManager.activePage?.data as PurchaseOrderSummaryTable;

    if (table) await table.refreshData(1);
  }

  public async doAfterPurchaseOrderEdit() {
    await this.updatePageTotals();
  }

  createTabManager(): TabManager {
    // build static pages for each of the configured table settings
    const getInitialPages = (): TabPage[] => {
      return this.tables.map(table => {
        const page: TabPage = {
          caption: () => table.eventTitle(),
          onEnter: () => {
            //enable data load the first time we enter the tab, but not before
            table.delayedDataLoad = false;
            // not-awaiting on purpose, for faster page switch. safe to do
            if (table.dataIsStale) table.refreshData();
          },
          content: () => {
            return table;
          },
          data: table,
          fragment: table.pageFragment
        };
        return page;
      });
    };
    return new TabManager({
      initialIndex: 0,
      actions: [
        {
          caption: () => html`${new IconRefresh()}`,
          onClick: async () => {
            const table = this.tabManager.activePage?.data as PurchaseOrderSummaryTable | undefined;
            if (!table) return;
            this.beforeRefresh();
            await table.refreshData();
            this.tables.forEach(t => {
              if (t !== table) {
                t.dataIsStale = true;
              }
            });
            this.afterRefresh();
          }
        }
      ],
      pages: () => getInitialPages()
    });
  }

  afterRefresh() {
    //nothing
  }

  beforeRefresh() {
    this.staleOrders = []; //nothing
  }

  protected async updatePageTotals() {
    const ownerId = await this.purchaseOrderOwnerId();

    const totals = await this.purchaseOrderApi.getTotalsByState({
      ownerId: ownerId,
      options: [
        {
          // eslint-disable-next-line no-bitwise
          state: PurchaseOrderState.Draft | PurchaseOrderState.Issued | PurchaseOrderState.IssuedPending,
          numberOfDaysHistory: null
        },
        // eslint-disable-next-line no-bitwise
        { state: PurchaseOrderState.Completed | PurchaseOrderState.Cancelled, numberOfDaysHistory: numberOfDaysHistory }
      ]
    });

    if (totals) this.purchaseOrderTotals = totals.totals.filter(x => x.ownerId === ownerId);

    this.tabManager.requestUpdate();
  }

  protected async purchaseOrderViewFactory(options: PurchaseOrderViewOptions): Promise<PurchaseOrderView> {
    return await constructAsync(new PurchaseOrderView(options));
  }

  //this can be overridden in sub class to replace the table with different column/view etc
  protected purchaseOrderSummaryTableFactory(options: PurchaseOrderSummaryTableOptions): PurchaseOrderSummaryTable {
    return new PurchaseOrderSummaryTable(options);
  }

  protected createSummaryTable(
    title: EventSnippet,
    purchaseOrderState: PurchaseOrderState,
    pageFragment: string,
    numberOfDays?: number
  ): PurchaseOrderSummaryTable {
    return this.purchaseOrderSummaryTableFactory({
      purchaseOrderState: purchaseOrderState,
      numberOfDaysHistory: numberOfDays,
      title: title,
      purchaseOrderOwnerId: this.purchaseOrderOwnerId,
      pageFragment: pageFragment,
      stale: { exists: (id: string) => this.staleOrders.includes(id) }
    });
  }

  protected createPurchaseOrderContainer(purchaseOrderId: string): PurchaseOrderContainer {
    return new PurchaseOrderContainer(purchaseOrderId, null, null);
  }

  //override this in a subclass to change the design, and order of the tables
  protected initializeTables(): PurchaseOrderSummaryTable[] {
    const title = (caption: string, state: PurchaseOrderState, hideTotal = false) => {
      let count = 0,
        total = 0;

      this.purchaseOrderTotals
        // eslint-disable-next-line no-bitwise,@typescript-eslint/no-unsafe-enum-comparison
        ?.filter(x => (state & x.state) === x.state)
        .forEach(x => {
          total += x.total;
          count += x.count;
        });

      return html`${caption} (${count})${hideTotal ? html`` : moneyToTemplateResult(total)}`;
    };
    const makeTable = (
      titleStr: () => string,
      state: PurchaseOrderState,
      pageFragment: string,
      numberOfDays?: number,
      hideTotal?: boolean
    ) => {
      return this.createSummaryTable(() => title(titleStr(), state, hideTotal), state, pageFragment, numberOfDays);
    };

    return [
      makeTable(
        () => LangPurchaseOrderListView.allCurrentTabTitle(),
        // eslint-disable-next-line no-bitwise
        PurchaseOrderState.Draft | PurchaseOrderState.Issued | PurchaseOrderState.IssuedPending,
        'allCurrent',
        undefined,
        true
      ),
      makeTable(() => LangPurchaseOrderListView.draftTabTitle(), PurchaseOrderState.Draft, 'draft', undefined, true),
      // eslint-disable-next-line no-bitwise
      makeTable(
        () => LangPurchaseOrderListView.issuedTabTitle(),
        PurchaseOrderState.Issued | PurchaseOrderState.IssuedPending,
        'issued',
        undefined,
        true
      ),
      makeTable(
        () => LangPurchaseOrderListView.completedTabTitle(),
        PurchaseOrderState.Completed,
        'completed',
        numberOfDaysHistory,
        true
      ),
      makeTable(
        () => LangPurchaseOrderListView.cancelledTabTitle(),
        PurchaseOrderState.Cancelled,
        'cancelled',
        numberOfDaysHistory,
        true
      )
    ];
  }

  protected async updateAndRefreshPurchaseOrderTab(switchToActiveTab?: boolean) {
    if (switchToActiveTab)
      if (this.tabManager.activeIndex !== 0) {
        this.tables[0].refreshData();
        await this.tabManager.setActiveIndex(0);
      } else await this.tables[0].refreshData();
    else await this.tables[this.tabManager.activeIndex]?.refreshData();
  }

  protected template(): EventTemplate {
    return html` <div id=${this.elementId} class="page-content">${this.tabManager?.ui}</div>`;
  }
}
